/**
 * Right-hand held grips (story `q-player-weapon-grip-holding-clips`, AD-838). DATA + one pure
 * derivation; no `three` import, so it runs under `node --test` (AD-120).
 *
 * The owner, 2026-10-23, on the live demo: the drawn sabre hung off an OPEN right hand, "obvious
 * and jarring" close up, worst while walking. Root cause: this rig has no finger joints (24
 * Meshy joints, nothing below `RightHand`), so no animation clip can close a hand: library or
 * custom. The only closed hand is a morph. Each held prop declares only WHERE ALONG ITS OWN HILT
 * the fist closes (`fistAt`, metres on the prop's local +Y, the grip frame every base and fitted
 * mesh is authored in) and WHICH fist closes on it; the seat follows: the prop's axis on the
 * fist's axis, `fistAt` on the fist's centre. A new right-hand prop is one row here; the grip
 * check (`gripContact.test.ts`) covers it automatically.
 *
 * q-grip-fist-inner-hand-see-through (owner phone playtest 2026-10-24, "inner hand is see
 * through"): AD-838 closed every prop in AD-384's `grip-r`, which is not a fist. Its axis runs
 * THROUGH the palm (along the palm normal), and its channel step left the closed hand an
 * inside-out sleeve 5.2 to 7.1 cm from that axis (12.6% of the hand's surface inverted) round a
 * sabre hilt of at most 2.5 cm, so from the front three-quarter and from behind you saw into the
 * hand and through it. The two fists now are the fingers bent round an axis parallel to the
 * knuckle line, on the palm side, thickness kept (`tools/gltf-grip-transplant/grip-r-fist.mjs`):
 * `grip-r-fist` closes on hilts and handles up to ~2.9 cm, `grip-r-haft` on the 3.5 to 4.5 cm wraps,
 * staffs and hafts.
 *
 * The Meshy library was searched for "holding" sets (2026-10-23): it has weapon-held turns,
 * backward walks, strafes and stances, but no forward walk or run with a sword, and every clip
 * is authored on the same finger-less skeleton, so none of them can close a hand either.
 */

import type { SocketTransform } from '../equipment/sockets.js';

/** The bone-local unit scale under the adopted Armature (cm bones, metre props). */
const UNIT = 100;

/** A closed fist in its hand bone's space (centimetres): its centre and the axis a prop's +Y takes. */
export interface GripCup {
  readonly centre: readonly [number, number, number];
  readonly rotation: readonly [number, number, number];
}

/**
 * The served `grip-r-fist` (hilts), in RightHand bone space (cm): `centre` is the middle of the
 * closed fist on its axis, `rotation` the Euler XYZ that sends a prop's local +Y along that axis
 * (toward the thumb) and its +Z along the palm normal. Written by the generator
 * (`grip-r-fist.mjs` metrics) and pinned against a fresh fit of the served bytes by
 * `gripContact.test.ts`.
 */
export const GRIP_CUP: GripCup = {
  centre: [5.4111, 13.3765, -0.641],
  rotation: [3.0393, 0.7225, 1.4969],
};

/** The served `grip-r-haft` (wraps, staffs, hafts): the same axis, a 4.6 cm palm radius. */
export const GRIP_CUP_HAFT: GripCup = {
  centre: [5.6818, 13.4605, -3.2069],
  rotation: [3.1133, 0.4289, 1.4561],
};

/** Which right-hand fist closes on a prop. */
export type RightFist = 'hilt' | 'haft';

export interface HeldGrip {
  /** Where the fist closes along the prop's own +Y (metres from its grip-frame origin). */
  fistAt?: number;
  /**
   * Where the hilt's axis crosses the prop's XZ plane (metres), when the mesh is not centred on
   * its own +Y (a fitted skin's grip can sit a few centimetres off it). Absent ⇒ on the axis.
   */
  hiltCentre?: readonly [number, number];
  /** The right-hand fist: `haft` for the thick grips (> ~2.9 cm radius). Absent ⇒ `hilt`. */
  fist?: RightFist;
  /** Turn of the prop about its own +Y, inside the fist (radians). Absent ⇒ 0. */
  roll?: number;
  /**
   * A small lean of the prop off the fist's axis, about its own X then Z (radians), inside the room
   * a thick fist leaves round a thinner haft (the grip check re-measures that the hand still clears
   * it). Absent ⇒ on the axis.
   */
  tip?: readonly [number, number];
  /** An explicit seat, for a prop no fist-on-hilt row can describe (none today). */
  seat?: SocketTransform;
}

/**
 * Every right-hand held prop, keyed by the rendered mesh id or by its archetype (a mesh row wins;
 * an archetype row covers every fitted mesh authored in that base's grip frame, skins included).
 * `fistAt` values are read off each mesh's hilt (see `gripContact.test.ts`, which re-checks that
 * the fist span lies on a hilt and nowhere else, and that the fist closes on it).
 */
export const HELD_GRIPS: Readonly<Record<string, HeldGrip>> = {
  // Weapons (weapon-bases-v1 archetypes).
  'sword-1h-base': { fistAt: 0.05 }, // hilt 0 to 0.16, guard from 0.16; 2.5 cm section
  // The staff leans 9 degrees inside the haft fist (its 3.8 cm haft in a 4.9 cm channel): on the
  // fist's own axis the far end crosses the chest in Attack_staff_2 (measured).
  'staff-base': { fistAt: 0.05, fist: 'haft', tip: [-0.1, 0.15] },
  'warhammer-2h-base': { fistAt: 0.05, fist: 'haft' },
  // The per-item warhammer's haft runs on 25 cm below the base's grip frame: same hold, at its butt.
  'iron-warhammer': { fistAt: -0.2, fist: 'haft' },
  // Per-item sword meshes (weapon-items-v1) sit lower in the grip frame than the base: a leather
  // wrap from -0.08 and the guard from 0.134 (0.0925 on the practice blade), 3.5 cm section.
  'bronze-shortsword': { fistAt: 0.02, fist: 'haft' },
  'copper-shortsword': { fistAt: 0.02, fist: 'haft' },
  'veinstone-blade': { fistAt: 0.02, fist: 'haft' },
  'driftwell-practice-blade': { fistAt: 0.0, fist: 'haft' },
  // Weapon skins (meshy-weapon-skins-v1), fitted onto their base's grip frame but not centred on
  // its axis: the emberbrand's banded grip sits 1.8 cm off it, the tidebinder's shaft 7 cm (and its
  // butt is a 13 cm ornament, so the fist closes higher up the shaft).
  'emberbrand-blade': { fistAt: 0.055, hiltCentre: [-0.018, 0.004] },
  'tidebinder-crook': { fistAt: 0.35, hiltCentre: [-0.031, 0.064], fist: 'haft' },
  // Gather tools (gather-tool-bases-v1), equipped in the mainhand: held near the butt.
  // handle 0 to 0.1, the blade leaves it at 0.1. Closer to the butt and turned 0.2 rad so the
  // thumb lies along the handle, not into its collar (q-gather-tool-one-in-hand: no hand vertex
  // deeper than 1.2 mm in the prop over idle, walk, run and the gather, on both bodies).
  'herbalist-sickle-base': { fistAt: 0.025, roll: -0.2 },
  'forestry-axe-base': { fistAt: 0.06 },
  'mining-pickaxe-base': { fistAt: 0.06 },
  'skinning-knife-base': { fistAt: 0.045 },
  'salvage-pry-base': { fistAt: 0.06 },
};

/** Euler XYZ (three.js order, R = Rx(a)·Ry(b)·Rz(c)) applied to a vector. */
function rotate(
  rotation: readonly [number, number, number],
  v: readonly [number, number, number],
): [number, number, number] {
  const [a, b, c] = rotation;
  const z = [
    v[0] * Math.cos(c) - v[1] * Math.sin(c),
    v[0] * Math.sin(c) + v[1] * Math.cos(c),
    v[2],
  ];
  const y = [
    z[0]! * Math.cos(b) + z[2]! * Math.sin(b),
    z[1]!,
    -z[0]! * Math.sin(b) + z[2]! * Math.cos(b),
  ];
  return [
    y[0]!,
    y[1]! * Math.cos(a) - y[2]! * Math.sin(a),
    y[1]! * Math.sin(a) + y[2]! * Math.cos(a),
  ];
}

/**
 * `rotation` followed (in the prop's own frame) by a turn of `roll` about its +Y and a lean `tip`
 * about its X then Z, as Euler XYZ: the matrix R·Ry(roll)·Rx(tip[0])·Rz(tip[1]), decomposed as
 * three.js `Euler.setFromRotationMatrix(m, 'XYZ')` does.
 */
function withTurn(
  rotation: readonly [number, number, number],
  roll: number,
  tip: readonly [number, number],
): [number, number, number] {
  const local = (v: readonly [number, number, number]): [number, number, number] => {
    const [x0, y0, z0] = v;
    // Rz(tip[1]), then Rx(tip[0]), then Ry(roll).
    const x1 = x0 * Math.cos(tip[1]) - y0 * Math.sin(tip[1]);
    const y1 = x0 * Math.sin(tip[1]) + y0 * Math.cos(tip[1]);
    const y2 = y1 * Math.cos(tip[0]) - z0 * Math.sin(tip[0]);
    const z2 = y1 * Math.sin(tip[0]) + z0 * Math.cos(tip[0]);
    return [
      x1 * Math.cos(roll) + z2 * Math.sin(roll),
      y2,
      -x1 * Math.sin(roll) + z2 * Math.cos(roll),
    ];
  };
  // Columns of R·L are R applied to L's basis vectors.
  const c0 = rotate(rotation, local([1, 0, 0]));
  const c1 = rotate(rotation, local([0, 1, 0]));
  const c2 = rotate(rotation, local([0, 0, 1]));
  const m13 = c2[0];
  const y = Math.asin(Math.max(-1, Math.min(1, m13)));
  if (Math.abs(m13) < 0.9999999) {
    return [Math.atan2(-c2[1], c2[2]), y, Math.atan2(-c1[0], c0[0])];
  }
  return [Math.atan2(c1[2], c1[1]), y, 0];
}

/** The held grip row for a rendered mesh, else for its archetype; `null` ⇒ not a closed-fist prop. */
export function heldGripFor(
  mesh: string | null | undefined,
  archetype?: string | null,
): HeldGrip | null {
  if (mesh != null && HELD_GRIPS[mesh] !== undefined) return HELD_GRIPS[mesh];
  if (archetype != null && HELD_GRIPS[archetype] !== undefined) return HELD_GRIPS[archetype];
  return null;
}

/** The right-hand fist a prop closes (`null` ⇒ the right hand stays open). */
export function rightFistFor(
  mesh: string | null | undefined,
  archetype?: string | null,
): RightFist | null {
  const row = heldGripFor(mesh, archetype);
  return row === null ? null : (row.fist ?? 'hilt');
}

/** The right-hand cup a row closes. */
export function rightCupFor(grip: HeldGrip): GripCup {
  return grip.fist === 'haft' ? GRIP_CUP_HAFT : GRIP_CUP;
}

/**
 * The bone seat that closes `cup` (default: the right-hand fist the row names) on the prop's hilt
 * at `fistAt` (and `hiltCentre`), turned by `roll` about the hilt and leaned by `tip`.
 */
export function heldGripSeat(grip: HeldGrip, cup: GripCup = rightCupFor(grip)): SocketTransform {
  if (grip.seat !== undefined) return grip.seat;
  const roll = grip.roll ?? 0;
  const tip = grip.tip ?? [0, 0];
  const rotation: [number, number, number] =
    roll === 0 && tip[0] === 0 && tip[1] === 0
      ? [cup.rotation[0], cup.rotation[1], cup.rotation[2]]
      : withTurn(cup.rotation, roll, tip);
  // The prop point that must land on the fist's centre, carried into bone space.
  const [hx, hz] = grip.hiltCentre ?? [0, 0];
  const hilt = rotate(rotation, [hx * UNIT, (grip.fistAt ?? 0) * UNIT, hz * UNIT]);
  return {
    position: [cup.centre[0] - hilt[0], cup.centre[1] - hilt[1], cup.centre[2] - hilt[2]],
    rotation,
    scale: UNIT,
  };
}

/* ------------------------------------------------------------ the left hand (q-bow-left-hand-grip) */

/**
 * The served `grip-l` fist, in LeftHand bone space (centimetres), measured like `GRIP_CUP` and
 * pinned against a fresh fit of the served bytes by `gripContact.test.ts`. The morph was authored
 * by Astra for a bow riser (story `q-bow-left-hand-grip`, `tools/gltf-grip-transplant/grip-l-morph.mjs`).
 */
export const GRIP_CUP_L: GripCup = {
  centre: [-1.2438, 14.6641, 0.0537],
  rotation: [0.5218, 0.4065, 0.595],
};

/**
 * Every LEFT-hand held prop (the left-lead bow, which inventory stores in mainhand but
 * `rigSocketPlacement` seats on LeftHand), keyed like `HELD_GRIPS`: a mesh row wins over its
 * archetype row. `fistAt` is the riser, read off each mesh.
 */
export const LEFT_HELD_GRIPS: Readonly<Record<string, HeldGrip>> = {
  'bow-base': { fistAt: 0.135, hiltCentre: [0, 0.0125] },
  'ashwood-longbow': { fistAt: 0.01 },
  'ironwood-warbow': { fistAt: 0, hiltCentre: [0, 0.0075] },
};

/** The left-hand held grip row for a rendered mesh, else for its archetype; `null` ⇒ open hand. */
export function leftHeldGripFor(
  mesh: string | null | undefined,
  archetype?: string | null,
): HeldGrip | null {
  if (mesh != null && LEFT_HELD_GRIPS[mesh] !== undefined) return LEFT_HELD_GRIPS[mesh];
  if (archetype != null && LEFT_HELD_GRIPS[archetype] !== undefined)
    return LEFT_HELD_GRIPS[archetype];
  return null;
}

/** The LeftHand-bone seat that closes `GRIP_CUP_L` on the prop's riser. */
export function leftHeldGripSeat(grip: HeldGrip): SocketTransform {
  return heldGripSeat(grip, GRIP_CUP_L);
}
